import React, { useEffect, useState } from "react";
import Icon from "../../lib/icon";
import './FilterTypes.scss';
import api from "../../api";

/* =========================================================
   FilterTypePrice
   ========================================================= */

export function FilterTypePriceShort({
  value = {},
  onChange
}) {
  const MIN = 0;
  const MAX = 100_000;
  const DEBOUNCE_MS = 300;

  // ----------------------------
  // local state (slider-only)
  // ----------------------------
  const [local, setLocal] = useState({
    from: value.from ?? MIN,
    to: value.to ?? MAX
  });

  // ----------------------------
  // sync from outside (route / clear / preset)
  // ----------------------------
  useEffect(() => {
    setLocal({
      from: value.from ?? MIN,
      to: value.to ?? MAX
    });
  }, [value.from, value.to]);

  // ----------------------------
  // debounce → global filters
  // ----------------------------
  useEffect(() => {
    const t = setTimeout(() => {
      onChange?.(local);
    }, DEBOUNCE_MS);

    return () => clearTimeout(t);
  }, [local.from, local.to]);

  // ----------------------------
  // handlers (NO global state)
  // ----------------------------
  function updateFrom(v) {
    setLocal(prev => ({
      ...prev,
      from: Math.min(v, prev.to)
    }));
  }

  function updateTo(v) {
    setLocal(prev => ({
      ...prev,
      to: Math.max(v, prev.from)
    }));
  }

  // ----------------------------
  // slider math
  // ----------------------------
  const left = (local.from / MAX) * 100;
  const right = (local.to / MAX) * 100;

  // ----------------------------
  // render
  // ----------------------------
  return (
    <div className="filter-short-card">
      <div className="filter-short-title">Цена, ₽</div>

      <div className="filter-price-short">
        <input value={local.from} readOnly />
        <input value={local.to} readOnly />
      </div>

      <div className="price-slider">
        <div className="price-slider__track" />
        <div
          className="price-slider__range"
          style={{
            left: `${left}%`,
            right: `${100 - right}%`
          }}
        />

        <input
          type="range"
          min={MIN}
          max={MAX}
          value={local.from}
          onChange={e => updateFrom(+e.target.value)}
          className="price-slider__thumb left"
        />

        <input
          type="range"
          min={MIN}
          max={MAX}
          value={local.to}
          onChange={e => updateTo(+e.target.value)}
          className="price-slider__thumb right"
        />
      </div>
    </div>
  );
}


/* =========================================================
   FilterTypeColor
   ========================================================= */

export function FilterTypeColorShort({
  filter,
  value = null,
  onChange
}) {
  const options = filter.meta?.values ?? [];
  const allActive = value == null;

  function select(val) {
    if (value === val) {
      onChange(null);
    } else {
      onChange(val);
    }
  }

  function selectAll() {
    if (value != null) {
      onChange(null);
    }
  }

  return (
    <div className="filter-short-card">
      <div className="filter-short-title">{filter.label}</div>

      <div className="filter-chips">
        {/* ALL */}
        <button
          type="button"
          className={`filter-chip ${allActive ? "active" : ""}`}
          onClick={selectAll}
        >
          <span>Все</span>
          {allActive && (
            <Icon
              name="close"
              width={20}
              height={20}
              color="#C6B2B6"
            />
          )}
        </button>

        {options.map(opt => {
          const active = value === opt.value;

          return (
            <button
              key={opt.value}
              type="button"
              className={`filter-chip ${active ? "active" : ""}`}
              onClick={() => select(opt.value)}
            >
              {opt.hex && (
                <span
                  className="color-dot"
                  style={{ background: opt.hex }}
                />
              )}
              <span>{opt.label}</span>

              {active && (
                <Icon
                  name="close"
                  width={20}
                  height={20}
                  color="#C6B2B6"
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* =========================================================
   FilterTypeSelect (Кому / Повод)
   ========================================================= */

export function FilterTypeSelectShort({
  filter,
  value = null,
  onChange
}) {
  const options = filter.meta?.values ?? [];
  const allActive = value == null;

  function select(val) {
    if (value === val) {
      onChange(null);
    } else {
      onChange(val);
    }
  }

  function selectAll() {
    if (value != null) {
      onChange(null);
    }
  }

  return (
    <div className="filter-short-card">
      <div className="filter-short-title">{filter.label}</div>

      <div className="filter-chips">
        {/* ALL */}
        <button
          type="button"
          className={`filter-chip ${allActive ? "active" : ""}`}
          onClick={selectAll}
        >
          <span>Все</span>
          {allActive && (
            <Icon
              name="close"
              width={20}
              height={20}
              color="#C6B2B6"
            />
          )}
        </button>

        {options.map(opt => {
          const active = value === opt.value;

          return (
            <button
              key={opt.value}
              type="button"
              className={`filter-chip ${active ? "active" : ""}`}
              onClick={() => select(opt.value)}
            >
              <span>{opt.label}</span>
              {active && (
                <Icon
                  name="close"
                  width={20}
                  height={20}
                  color="#C6B2B6"
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function FilterTypeCategoryShort({
  value = {},          // { categoryId, subcategoryId }
  onChange
}) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/api/categories/WithSubcategories")
      .then(r => setCategories(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="filter-short-card">
        <div className="filter-short-card__title">Категория</div>
        <div className="filter-skeleton">Загрузка…</div>
      </div>
    );
  }

  function selectAll() {
    onChange({ categoryId: null, subcategoryId: null });
  }

  function selectCategory(categoryId) {
    onChange({ categoryId, subcategoryId: null });
  }

  function selectSubcategory(categoryId, subcategoryId) {
    onChange({ categoryId, subcategoryId });
  }

  return (
    <div className="filter-short-card non-border">
      <div className="filter-category-list">
        {/* Все категории */}
        {/** 
        <div
          className="filter-radio-row"
          onClick={selectAll}
        >
          <span className="filter-radio-label">Все категории</span>
          <input
            type="radio"
            checked={!value}
            readOnly
          />
        </div>
        */}
        {categories.map(cat => {
          const categorySelected =
            value === cat.id;

          return (
            <div key={cat.id} className="filter-category-block">
              {/* Категория */}
              <div
                className="filter-radio-row"
                onClick={() => selectCategory(cat.id)}
              >
                <span className="filter-radio-label">
                  {cat.name}
                </span>
                <input
                  type="radio"
                  checked={categorySelected}
                  readOnly
                />
              </div>

              {/* Подкатегории — всегда раскрыты */}
              {cat.subcategories?.length > 0 && (
                <div className="filter-subcategory-list">
                  {cat.subcategories.map(sc => (
                    <div
                      key={sc.id}
                      className="filter-radio-row sub"
                      onClick={() =>
                        selectSubcategory(cat.id, sc.id)
                      }
                    >
                      <span className="filter-radio-label">
                        {sc.name}
                      </span>
                      <input
                        type="radio"
                        checked={value === sc.id}
                        readOnly
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function FilterTypeMarketShort({
  value = [],
  onChange
}) {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);

  // ----------------------------
  // load markets
  // ----------------------------
  useEffect(() => {
    api
      .get("/api/Market/GetLastMarkets")
      .then(r => setMarkets(r.data ?? []))
      .finally(() => setLoading(false));
  }, []);

  // ----------------------------
  // helpers
  // ----------------------------
  function isSelected(id) {
    return value.some(x => x.value === id);
  }

  function toggleMarket(market) {
    const exists = value.find(x => x.value === market.id);

    if (exists) {
      onChange(value.filter(x => x.value !== market.id));
    } else {
      onChange([
        ...value,
        { value: market.id, label: market.name }
      ]);
    }
  }

  function clearAll() {
    if (value.length > 0) {
      onChange([]);
    }
  }

  // ----------------------------
  // render
  // ----------------------------
  return (
    <div className="filter-short-card non-border">
      <div className="filter-short-title">Продавец</div>

      {loading && (
        <div className="filter-skeleton">Загрузка…</div>
      )}

      {!loading && (
        <div className="filter-chips">
          {markets.map(market => {
            const active = isSelected(market.id);

            return (
              <button
                key={market.id}
                type="button"
                className={`filter-chip ${active ? "active" : ""}`}
                onClick={() => toggleMarket(market)}
              >
                <span>{market.name}</span>
                {active && (
                  <Icon
                    name="close"
                    width={20}
                    height={20}
                    color="#C6B2B6"
                  />
                )}
              </button>
            );
          })}

          {/* ALL */}
          <button
            type="button"
            className={`filter-chip ${value.length === 0 ? "active" : ""}`}
            onClick={clearAll}
          >
            <span>Все</span>
            {value.length === 0 && (
              <Icon
                name="close"
                width={20}
                height={20}
                color="#C6B2B6"
              />
            )}
          </button>
        </div>
      )}
    </div>
  );
}

const DELIVERY_OPTIONS = [
  { value: 30, label: "до 30 мин" },
  { value: 45, label: "до 45 мин" },
  { value: 60, label: "до 1 часа" },
  { value: 999, label: "больше 1 часа" }
];

export function FilterTypeDeliveryShort({
  value = null,
  onChange
}) {
  function select(val) {
    if (value === val) {
      onChange(null);
    } else {
      onChange(val);
    }
  }

  return (
    <div className="filter-short-card">
      <div className="filter-short-title">Время доставки</div>

      <div className="filter-chips">
        {DELIVERY_OPTIONS.map(opt => {
          const active = value === opt.value;

          return (
            <button
              key={opt.value}
              type="button"
              className={`filter-chip ${active ? "active" : ""}`}
              onClick={() => select(opt.value)}
            >
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const RATING_OPTIONS = [
  { value: 4.6, label: "от 4.6" },
  { value: 4.7, label: "от 4.7" },
  { value: 4.8, label: "от 4.8" },
  { value: 4.9, label: "от 4.9" },
  { value: 5.0, label: "5.0" }
];

export function FilterTypeRatingShort({
  value = null,
  onChange
}) {
  function select(val) {
    if (value === val) {
      onChange(null);
    } else {
      onChange(val);
    }
  }

  return (
    <div className="filter-short-card">
      <div className="filter-short-title">Рейтинг</div>

      <div className="filter-chips">
        {RATING_OPTIONS.map(opt => {
          const active = value === opt.value;

          return (
            <button
              key={opt.value}
              type="button"
              className={`filter-chip ${active ? "active" : ""}`}
              onClick={() => select(opt.value)}
            >
              <Icon
                name="star"
                width={16}
                height={16}
                color={active ? "#fff" : "#C6B2B6"}
              />
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const WORKTIME_OPTIONS = [
  { value: "open_now", label: "Открыто сейчас" },
  { value: "24h", label: "Круглосуточно" }
];

export function FilterTypeWorkTimeShort({ value = null, onChange }) {
  function select(v) {
    onChange(value === v ? null : v);
  }

  return (
    <div className="filter-short-card">
      <div className="filter-short-title">Время работы</div>

      <div className="filter-chips">
        {WORKTIME_OPTIONS.map(opt => (
          <button
            key={opt.value}
            className={`filter-chip ${value === opt.value ? "active" : ""}`}
            onClick={() => select(opt.value)}
          >
            <span>{opt.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* =========================================================
   Универсальный switch
   ========================================================= */

export function FilterTypeShortRenderer({ filter, value, onOpenFull, onChangeCategory, onChange }) {
  switch (filter.type) {
    case "Number":
      return (
        <FilterTypePriceShort
          filter={filter}
          value={value}
          onChange={onChange}
          onOpenFull={onOpenFull}
        />
      );

    case "MultiSelect":
      return (
        <FilterTypeColorShort
          filter={filter}
          value={value}
          onChange={onChange}
          onOpenFull={onOpenFull}
        />
      );
    case "Market":
      return (
        <FilterTypeMarketShort
          value={value}
          onChange={onChange}
        />
      );
    case "Select":
      return (
        <FilterTypeSelectShort
          filter={filter}
          value={value}
          onChange={onChange}
          onOpenFull={onOpenFull}
        />
      );
      case "Category":
        return (
          <FilterTypeCategoryShort
            value={value}
            onChange={onChangeCategory}
          />
        );
      case "Delivery":
        return (
          <FilterTypeDeliveryShort
            value={value}
            onChange={onChange}
          />
        );

      case "Static":
        return (
          <FilterTypeRatingShort
            value={value}
            onChange={onChange}
          />
        );

      case "WorkTime":
        return <FilterTypeWorkTimeShort value={value} onChange={onChange} />;
        
    default:
      return null;
  }
}